import React from 'react';
import { Route } from 'react-router-dom';

import { lazy } from '@components/common/routeLoading';
import { Path } from '@components/routes/constants';
import { entitled } from '@components/routes/routeEntitlement';
import { useHasDataGraphAudiences } from '@components/routes/entitlements';
import { SECTION } from '@hooks/useDocumentTitle/constants';

/**
 * Every element is lazy — see the note in @components/cohorts/routes on why the
 * route definitions themselves must not reference page modules directly.
 */
const DataGraphAudiencesLayout = lazy(() =>
  import(/* webpackChunkName: "dataGraphAudiences" */ './routesLayout').then((module) => ({
    default: module.DataGraphAudiencesLayout,
  })),
);

const AudienceListPage = lazy(() =>
  import(/* webpackChunkName: "dataGraphAudiences" */ './audienceListPage').then((module) => ({
    default: module.AudienceListPage,
  })),
);

const AudienceDetailPage = lazy(() =>
  import(/* webpackChunkName: "dataGraphAudiences" */ './audienceDetailPage').then((module) => ({
    default: module.AudienceDetailPage,
  })),
);

const SyncDetailsPage = lazy(() =>
  import(/* webpackChunkName: "dataGraphAudiences" */ './syncDetailsPage').then((module) => ({
    default: module.SyncDetailsPage,
  })),
);

/** See the note in @components/botManagement/routes on why these are definitions. */
export const dataGraphAudienceRoutes = (
  <Route
    key={Path.DATA_GRAPH_AUDIENCES}
    path={Path.DATA_GRAPH_AUDIENCES}
    element={entitled(useHasDataGraphAudiences, <DataGraphAudiencesLayout />)}
    handle={{ section: SECTION.dataGraphAudiences }}
  >
    <Route index element={<AudienceListPage />} />
    <Route path=":id" element={<AudienceDetailPage />} />
    {/* Its own section rather than an action on the parent: the page is named by
        the audience→destination pair, so `Weekly Actives → Braze - Data graph
        audience syncs` reads better than folding it under the audience. */}
    <Route
      path=":audienceId/syncs/:syncId"
      element={<SyncDetailsPage />}
      handle={{ section: SECTION.dataGraphAudienceSyncs }}
    />
  </Route>
);
